import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetAdQuery, useGetAdChartsQuery, useToggleAdStatusMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Image from '../../../components/Image';
import BarChart from '../../../components/charts/BarChart';
import PieChart from '../../../components/charts/PieChart';
import env from '../../../config/env';
import { getStatusColor, formatCurrency } from '../../../utils/helpers';
import Swal from 'sweetalert2';

// Format date as "d M, Y" (e.g., "27 Mar, 2023")
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const AdminAdDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useGetAdQuery(id!);
  const { data: chartsData } = useGetAdChartsQuery(id!);
  const [toggleStatus] = useToggleAdStatusMutation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getStatusButtonClass = (status: string | null | undefined): string => {
    const currentStatus = status || 'Pending';
    switch (currentStatus) {
      case 'Active':
        return 'btn btn-success dropdown-toggle';
      case 'Inactive':
        return 'btn btn-secondary dropdown-toggle';
      case 'Blocked':
        return 'btn btn-danger dropdown-toggle';
      case 'Pending':
      default:
        return 'btn btn-warning dropdown-toggle';
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    setIsDropdownOpen(false);
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `Do you want to change the status to ${newStatus}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, update it!'
      });

      if (result.isConfirmed) {
        await toggleStatus({ id, status: newStatus }).unwrap();
        Swal.fire(
          'Updated!',
          `Ad status has been updated to ${newStatus}.`,
          'success'
        );
      }
    } catch (err: any) {
      Swal.fire(
        'Error!',
        err?.data?.message || 'Failed to update ad status.',
        'error'
      );
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !data?.data) {
    return (
      <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
        <div className="d-flex flex-column flex-column-fluid">
          <div className="alert alert-danger m-5">
            {error ? 'Error loading ad details' : 'Ad not found'}
          </div>
        </div>
      </div>
    );
  }

  const ad = data.data;
  const analytics = ad.analytics;
  const imageBaseUrl = env.apiUrl.replace('/api', '');
  const adPerformance = analytics?.ad_performance;
  const engagementMetrics = analytics?.engagement_metrics;
  const conversionAndContact = analytics?.conversion_and_contact;
  const financialMetrics = analytics?.financial_metrics;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Ad Details</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/ads'); }} className="text-muted text-hover-primary">Ads</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Details</li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <div className="dropdown" ref={dropdownRef}>
                <button 
                  className={getStatusButtonClass(ad.status)} 
                  type="button" 
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  aria-expanded={isDropdownOpen}
                >
                  {ad.status || 'Pending'}
                </button>
                <ul 
                  className={`dropdown-menu ${isDropdownOpen ? 'show' : ''}`} 
                  style={isDropdownOpen ? { display: 'block' } : {}}
                >
                  <li>
                    <button 
                      type="button"
                      className="dropdown-item d-flex align-items-center" 
                      onClick={() => handleStatusChange('Pending')}
                    >
                      <span className="bullet bullet-dot bg-warning me-2 h-10px w-10px"></span>
                      Pending
                    </button>
                  </li>
                  <li>
                    <button 
                      type="button"
                      className="dropdown-item d-flex align-items-center" 
                      onClick={() => handleStatusChange('Draft')}
                    >
                      <span className="bullet bullet-dot bg-secondary me-2 h-10px w-10px"></span>
                      Draft
                    </button>
                  </li>
                  <li>
                    <button 
                      type="button"
                      className="dropdown-item d-flex align-items-center" 
                      onClick={() => handleStatusChange('Active')}
                    >
                      <span className="bullet bullet-dot bg-success me-2 h-10px w-10px"></span>
                      Active
                    </button>
                  </li>
                  <li>
                    <button 
                      type="button"
                      className="dropdown-item d-flex align-items-center" 
                      onClick={() => handleStatusChange('Inactive')}
                    >
                      <span className="bullet bullet-dot bg-warning me-2 h-10px w-10px"></span>
                      Inactive
                    </button>
                  </li>
                  <li>
                    <button 
                      type="button"
                      className="dropdown-item d-flex align-items-center" 
                      onClick={() => handleStatusChange('Blocked')}
                    >
                      <span className="bullet bullet-dot bg-danger me-2 h-10px w-10px"></span>
                      Blocked
                    </button>
                  </li>
                </ul>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => navigate('/admin/ads')}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back to List
              </button>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="row g-5 g-xl-10">
              {/* Main Content */}
              <div className="col-xl-8">
                {/* Basic Information Card */}
                <div className="card mb-5 mb-xl-10">
                  <div className="card-header border-0 pt-6">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold fs-3 mb-1">Basic Information</span>
                    </h3>
                  </div>
                  <div className="card-body pt-0">
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">Title</label>
                      <div className="col-lg-8">
                        <span className="fw-bold fs-6 text-gray-800">{ad.title || 'N/A'}</span>
                      </div>
                    </div>
                    {ad.description && (
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">Description</label>
                        <div className="col-lg-8">
                          <span className="fw-normal fs-6 text-gray-600">{ad.description}</span>
                        </div>
                      </div>
                    )}
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">Category</label>
                      <div className="col-lg-8">
                        <span className="badge badge-light-primary fs-7">
                          {ad.ad_category?.title || 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">Ad Destination </label>
                      <div className="col-lg-8">
                        <span className=" text-uppercase fs-7">
                          {ad.ad_destination || 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">Type</label>
                      <div className="col-lg-8">
                        <span className="badge badge-light-info fs-7">
                          {ad.ad_type?.title || 'N/A'}
                        </span>
                      </div>
                    </div>
                    {ad.strategic_ad_type && (
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">Strategic Type</label>
                        <div className="col-lg-8">
                          <span className="badge badge-light-info fs-7">
                            {ad.strategic_ad_type?.title || 'N/A'}
                          </span>
                        </div>
                      </div>
                    )}
                    {ad?.meta_data && ad.meta_data.cta_button_text && (
                      <div className="row mb-7" >
                        <label className="col-lg-4 fw-semibold text-muted">
                          CTA Button Text
                        </label>
                        <div className="col-lg-8">
                              <span className="fw-bold badge bg-light-primary text-dark">{ad.meta_data.cta_button_text || 'N/A'}</span>
                        </div>
                      </div>
                    )}
                    {ad?.external_link && ad.meta_data.external_link && (
                      <div className="row mb-7" >
                        <label className="col-lg-4 fw-semibold text-muted">
                          Target Ad
                        </label>
                        <div className="col-lg-8">
                            <a href={ad.external_link} target="_blank" rel="noopener noreferrer">
                              <span className="fw-bold badge bg-light-primary text-dark">{ad.external_link || 'N/A'}</span>
                            </a>
                        </div>
                      </div>
                    )}
                    {ad?.meta_data && ad.meta_data.target_ad && (
                      <div className="row mb-7" >
                        <label className="col-lg-4 fw-semibold text-muted">
                          Target Ad
                        </label>
                        <div className="col-lg-8">
                              <span className="fw-bold badge bg-light-primary text-dark">#{ad.meta_data.target_ad.ad_id || 'N/A'}</span>
                              <p className="fw-bold text-gray-800">{ad.meta_data.target_ad.title || 'N/A'}</p>
                        </div>
                      </div>
                    )}
                    {ad?.meta_data && ad.meta_data.notification_title && (
                      <div className="row mb-7" >
                        <label className="col-lg-4 fw-semibold text-muted">
                          Notification Title
                        </label>
                        <div className="col-lg-8">
                              <p className="mb-1 fw-bold text-dark">{ad.meta_data.notification_title || 'N/A'}</p>
                              <p className=" text-muted">{ad.meta_data.notification_description || 'N/A'}</p>
                        </div>
                      </div>
                    )}

                    {ad.ad_package && (
                      <div className="row mb-7">
                        <label className="col-lg-4 fw-semibold text-muted">Package</label>
                        <div className="col-lg-8">
                            <span className="badge badge-light-warning fs-6 mb-1">
                              {ad.ad_package.title || 'N/A'}
                            </span> <br />
                            <p className="fw-bold mb-1 text-gray-700">Payment Method: {ad.payment_method || 'N/A'}</p>
                            <p className="fw-bold mb-1 text-gray-700">Transaction ID: {ad.transaction_id || 'N/A'}</p>
                            <p className="fw-bold mb-1 text-gray-900">Pay Amount: {formatCurrency(ad.payment_amount)}</p>
                        </div>
                      </div>
                    )}
                   
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">Language</label>
                      <div className="col-lg-8">
                        <span className="badge badge-light-success fs-7">
                          {ad.ad_language?.toUpperCase() || 'EN'}
                        </span>
                      </div>
                    </div>
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">Same Content</label>
                      <div className="col-lg-8">
                        <span className={`badge ${ad.same_content ? 'badge-light-success' : 'badge-light-danger'} fs-7`}>
                          {ad.same_content ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                    <div className="row mb-7">
                      <label className="col-lg-4 fw-semibold text-muted">Status</label>
                      <div className="col-lg-8">
                        <span className={`badge ${getStatusColor(ad.status)} fs-7`}>
                          {ad.status }
                        </span>
                      </div>
                    </div>
                    <div className="row">
                      <label className="col-lg-4 fw-semibold text-muted">Created Date</label>
                      <div className="col-lg-8">
                        <span className="fw-normal fs-6 text-gray-600">{formatDate(ad.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images Card */}
                {ad.images && Array.isArray(ad.images) && ad.images.length > 0 && (
                  <div className="card mb-5 mb-xl-10">
                    <div className="card-header border-0 pt-6">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold fs-3 mb-1">Images</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">{ad.images.length} image(s)</span>
                      </h3>
                    </div>
                    <div className="card-body pt-0">
                      <div className="row g-3">
                        {ad.images.map((image: string, index: number) => {
                          return (
                            <div key={index} className="col-md-4">
                              <div className="card-rounded overflow-hidden border border-gray-200">
                                <Image 
                                  src={image} 
                                  alt={`Ad image ${index + 1}`}
                                  className="w-100 h-200px object-fit-cover"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Custom Fields Card */}
                {ad.custom_fields && ad.custom_fields.length > 0 && (
                  <div className="card mb-5 mb-xl-10">
                    <div className="card-header border-0 pt-6">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold fs-3 mb-1">Ad Details</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">{ad.custom_fields.length} field(s)</span>
                      </h3>
                    </div>
                    <div className="card-body pt-0">
                      <div className="table-responsive">
                        <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                          <thead>
                            <tr className="fw-bold text-muted">
                              <th className="min-w-150px"> Name</th>
                              <th className="min-w-200px">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ad.custom_fields.map((field: any) => {
                              // Handle both pivot.value and direct value access
                              const fieldValue = field.pivot?.value || field.value || 'N/A';
                              return (
                                <tr key={field.id}>
                                  <td>
                                    <span className="text-gray-800 fw-bold">{field.field_name || 'N/A'}</span>
                                  </td>
                                 
                                  <td>
                                    {(() => {
                                      if (field.field_type === 'file_multiple' && Array.isArray(fieldValue)) {
                                        return (
                                          <div className="row g-3">
                                            {fieldValue.map((file: string, index: number) => {
                                              return (
                                                <div key={index} className="col-md-4">
                                                  <div className="card-rounded overflow-hidden border border-gray-200">
                                                    <Image 
                                                      src={file} 
                                                      alt={`Ad image ${index + 1}`}
                                                      className="w-100 h-200px object-fit-cover"
                                                    />
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        );
                                      } else if (field.field_type === 'file_single') {
                                        return (
                                          <div className="card-rounded overflow-hidden border border-gray-200" style={{ maxWidth: '200px' }}>
                                            <Image 
                                              src={fieldValue} 
                                              alt={String(fieldValue)}
                                              className="w-100 object-fit-cover"
                                              style={{ height: '150px' }}
                                            />
                                          </div>
                                        );
                                      }
                                      else if (field.field_type === 'dropdown_multiple') {
                                        return (
                                          <>
                                            {fieldValue?.split(',')?.map((value: string, index: number) => (
                                              <span key={index} className="badge bg-secondary text-dark m-1" >
                                                {value}
                                              </span>
                                            ))}
                                          </>
                                        );
                                      } else {
                                        return <span className="text-gray-800">{String(fieldValue)}</span>;
                                      }
                                    })()}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="col-xl-4">
                <div className="card mb-5 mb-xl-10">
                  <div className="card-header border-0 pt-6">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold fs-3 mb-1">Analytics</span>
                    </h3>
                  </div>
                  <div className="card-body pt-0">
                    {analytics ? (
                      <>
                        <div className="mb-7">
                          <h4 className="fw-bold text-gray-800 mb-3">Ad Performance</h4>
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Total Views</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.total_views ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Reach</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.reach ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Total Clicks</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.total_clicks ?? '-'}
                              </span>
                            </div>
                            {/* Outbound Clicks - Only for Strategic Ads */}
                            {ad.strategic_ad_type && (
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">Outbound Clicks</span>
                                <span className="fw-bold text-gray-800">
                                  {adPerformance?.outbound_clicks ?? '-'}
                                </span>
                              </div>
                            )}
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Ad Duration</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.ad_duration ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Remaining Duration</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.remaining_duration ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Bounce Rate</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.bounce_rate ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Repeat Visitors</span>
                              <span className="fw-bold text-gray-800">
                                {adPerformance?.repeat_visitors ?? '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="mb-7">
                          <h4 className="fw-bold text-gray-800 mb-3">Engagement Metrics</h4>
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Favorites</span>
                              <span className="fw-bold text-gray-800">
                                {engagementMetrics?.favorites ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Shares</span>
                              <span className="fw-bold text-gray-800">
                                {engagementMetrics?.shares ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Comments</span>
                              <span className="fw-bold text-gray-800">
                                {engagementMetrics?.comments ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Engagement Score</span>
                              <span className="fw-bold text-gray-800">
                                {engagementMetrics?.engagement_score ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Scroll Depth</span>
                              <span className="fw-bold text-gray-800">
                                {engagementMetrics?.scroll_depth ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Average Time on Ad</span>
                              <span className="fw-bold text-gray-800">
                                {engagementMetrics?.average_time_on ?? '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="mb-7">
                          <h4 className="fw-bold text-gray-800 mb-3">Conversion & Contact</h4>
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Messages</span>
                              <span className="fw-bold text-gray-800">
                                {conversionAndContact?.messages ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Cost Per Message</span>
                              <span className="fw-bold text-gray-800">
                                {conversionAndContact?.cost_per_message ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Conversion Rate</span>
                              <span className="fw-bold text-gray-800">
                                {conversionAndContact?.conversion_rate ?? '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h4 className="fw-bold text-gray-800 mb-3">Financial Metrics</h4>
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Total Spending</span>
                              <span className="fw-bold text-gray-800">
                                {financialMetrics?.total_spending ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Average CPC</span>
                              <span className="fw-bold text-gray-800">
                                {financialMetrics?.average_cpc ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Average CPM</span>
                              <span className="fw-bold text-gray-800">
                                {financialMetrics?.average_cpm ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Cost Per Contact</span>
                              <span className="fw-bold text-gray-800">
                                {financialMetrics?.cost_per_contact ?? '-'}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Click Through Rate</span>
                              <span className="fw-bold text-gray-800">
                                {financialMetrics?.click_through_rate ?? '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-muted">No analytics data available.</div>
                    )}
                  </div>
                </div>

                {/* User Information Card */}
                <div className="card mb-5 mb-xl-10">
                  <div className="card-header border-0 pt-6">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold fs-3 mb-1">User Information</span>
                    </h3>
                  </div>
                  <div className="card-body pt-0">
                    <div className="mb-7">
                      <label className="fw-semibold text-muted mb-2">Name</label>
                      <div className="fw-bold fs-6 text-gray-800">{ad.user?.name || 'N/A'}</div>
                    </div>
                    {ad.user?.email && (
                      <div className="mb-7">
                        <label className="fw-semibold text-muted mb-2">Email</label>
                        <div className="fw-normal fs-6 text-gray-600">{ad.user.email}</div>
                      </div>
                    )}
                    {ad.user?.phone && (
                      <div className="mb-7">
                        <label className="fw-semibold text-muted mb-2">Phone</label>
                        <div className="fw-normal fs-6 text-gray-600">{ad.user.phone}</div>
                      </div>
                    )}
                    <div className="mb-7">
                      <label className="fw-semibold text-muted mb-2">Classification</label>
                      <div>
                        <span className="badge badge-light-info fs-7 fw-bold text-capitalize">
                          {ad.user?.user_type ? ad.user.user_type.replace(/_/g, ' ') : 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="mb-0">
                      <label className="fw-semibold text-muted mb-2">Verification</label>
                      <div>
                        <span className={`badge fs-7 fw-bold ${
                          ad.user?.verification_badge?.includes('Green') ? 'badge-light-success' :
                          ad.user?.verification_badge?.includes('Gold') ? 'badge-light-warning' :
                          ad.user?.verification_badge?.startsWith('Requested') ? 'badge-light-warning' :
                          ad.user?.verification_badge?.startsWith('Rejected') ? 'badge-light-danger' :
                          'badge-light-secondary text-dark'
                        }`}>
                          {ad.user?.verification_badge || 'Unverified'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Countries Card */}
                {ad.countries && ad.countries.length > 0 && (
                  <div className="card mb-5 mb-xl-10">
                    <div className="card-header border-0 pt-6">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold fs-3 mb-1">Countries</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">{ad.countries.length} country/countries</span>
                      </h3>
                    </div>
                    <div className="card-body pt-0">
                      <div className="d-flex flex-wrap gap-2">
                        {ad.countries.map((country: any) => (
                          <span key={country.id} className="badge badge-light-primary fs-7">
                            {country.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Cities Card */}
                {ad.cities && ad.cities.length > 0 && (
                  <div className="card mb-5 mb-xl-10">
                    <div className="card-header border-0 pt-6">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold fs-3 mb-1">Cities</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">{ad.cities.length} city/cities</span>
                      </h3>
                    </div>
                    <div className="card-body pt-0">
                      <div className="d-flex flex-wrap gap-2">
                        {ad.cities.map((city: any) => (
                          <span key={city.id} className="badge badge-light-info fs-7">
                            {city.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}


              </div>
            </div>

            {/* Analytics Charts Row */}
            {analytics && (
              <div className="row g-5 g-xl-10 mt-2">

                {/* Performance Bar Chart */}
                <div className="col-12 col-xl-8">
                  <div className="card card-flush">
                    <div className="card-header pt-5">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold text-dark">Performance Overview</span>
                        <span className="text-gray-400 mt-1 fw-semibold fs-6">Views, clicks, reach, shares &amp; engagement</span>
                      </h3>
                    </div>
                    <div className="card-body d-flex align-items-end p-0" style={{ minHeight: 280 }}>
                      <BarChart
                        labels={['Views', 'Clicks', 'Reach', 'Shares', 'Comments', 'Favorites']}
                        datasets={[{
                          label: 'Count',
                          data: [
                            ad.ad_stat?.views ?? 0,
                            ad.ad_stat?.clicks ?? 0,
                            ad.ad_stat?.reach ?? 0,
                            ad.ad_stat?.shares ?? 0,
                            ad.ad_stat?.comments ?? 0,
                            parseInt(engagementMetrics?.favorites ?? '0'),
                          ],
                          backgroundColor: 'rgba(57, 106, 84, 0.75)',
                          borderColor: 'rgba(57, 106, 84, 1)',
                        }]}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: {
                            y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' } },
                            x: { grid: { display: false } },
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Engagement Doughnut */}
                <div className="col-12 col-xl-4">
                  <div className="card card-flush h-100">
                    <div className="card-header pt-5">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold text-dark">Engagement Breakdown</span>
                        <span className="text-gray-400 mt-1 fw-semibold fs-6">Shares, comments &amp; favorites</span>
                      </h3>
                    </div>
                    <div className="card-body d-flex align-items-center justify-content-center pt-0 pb-5" style={{ minHeight: 240 }}>
                      {(ad.ad_stat?.shares || ad.ad_stat?.comments || engagementMetrics?.favorites) ? (
                        <div style={{ width: '100%', maxWidth: 280, height: 220 }}>
                          <PieChart
                            labels={['Shares', 'Comments', 'Favorites']}
                            data={[
                              ad.ad_stat?.shares ?? 0,
                              ad.ad_stat?.comments ?? 0,
                              parseInt(engagementMetrics?.favorites ?? '0'),
                            ]}
                            backgroundColor={[
                              'rgba(57, 106, 84, 0.85)',
                              'rgba(57, 106, 84, 0.50)',
                              'rgba(33, 37, 41, 0.65)',
                            ]}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: {
                                  display: true,
                                  position: 'bottom' as const,
                                  labels: { boxWidth: 12, padding: 12, font: { size: 11 }, usePointStyle: true },
                                },
                              },
                            }}
                          />
                        </div>
                      ) : (
                        <span className="text-muted">No engagement data yet</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Views & Clicks Timeline Chart */}
                {chartsData?.data?.timeline?.labels?.length > 0 && (
                  <div className="col-12">
                    <div className="card card-flush">
                      <div className="card-header pt-5">
                        <h3 className="card-title align-items-start flex-column">
                          <span className="card-label fw-bold text-dark">Views &amp; Clicks Over Time</span>
                          <span className="text-gray-400 mt-1 fw-semibold fs-6">Daily/weekly activity timeline</span>
                        </h3>
                      </div>
                      <div className="card-body d-flex align-items-end p-0" style={{ minHeight: 280 }}>
                        <BarChart
                          labels={chartsData.data.timeline.labels}
                          datasets={[
                            {
                              label: 'Views',
                              data: chartsData.data.timeline.views,
                              backgroundColor: 'rgba(57, 106, 84, 0.75)',
                              borderColor: 'rgba(57, 106, 84, 1)',
                            },
                            {
                              label: 'Clicks',
                              data: chartsData.data.timeline.clicks,
                              backgroundColor: 'rgba(100, 116, 139, 0.55)',
                              borderColor: 'rgba(100, 116, 139, 0.85)',
                            },
                          ]}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { display: true, position: 'top' as const },
                            },
                            scales: {
                              y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' } },
                              x: { grid: { display: false } },
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Views & Clicks by Country */}
                {(chartsData?.data?.by_country?.views?.length > 0 || chartsData?.data?.by_country?.clicks?.length > 0) && (
                  <div className="col-12">
                    <div className="card card-flush">
                      <div className="card-header pt-5">
                        <h3 className="card-title align-items-start flex-column">
                          <span className="card-label fw-bold text-dark">Audience by Country</span>
                          <span className="text-gray-400 mt-1 fw-semibold fs-6">Views and clicks per user country</span>
                        </h3>
                      </div>
                      <div className="card-body d-flex align-items-end p-0" style={{ minHeight: 280 }}>
                        {(() => {
                          const viewsArr: { country: string; total: number }[] = chartsData.data.by_country.views || [];
                          const clicksArr: { country: string; total: number }[] = chartsData.data.by_country.clicks || [];
                          const allCountries = Array.from(new Set([
                            ...viewsArr.map((v) => v.country),
                            ...clicksArr.map((c) => c.country),
                          ]));
                          const viewsMap = Object.fromEntries(viewsArr.map((v) => [v.country, v.total]));
                          const clicksMap = Object.fromEntries(clicksArr.map((c) => [c.country, c.total]));
                          return (
                            <BarChart
                              labels={allCountries}
                              datasets={[
                                {
                                  label: 'Views',
                                  data: allCountries.map((c) => viewsMap[c] ?? 0),
                                  backgroundColor: 'rgba(57, 106, 84, 0.75)',
                                  borderColor: 'rgba(57, 106, 84, 1)',
                                },
                                {
                                  label: 'Clicks',
                                  data: allCountries.map((c) => clicksMap[c] ?? 0),
                                  backgroundColor: 'rgba(100, 116, 139, 0.55)',
                                  borderColor: 'rgba(100, 116, 139, 0.85)',
                                },
                              ]}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: { display: true, position: 'top' as const },
                                },
                                scales: {
                                  y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' } },
                                  x: { grid: { display: false } },
                                },
                              }}
                            />
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Rate Metrics — progress bars */}
                <div className="col-12">
                  <div className="card card-flush">
                    <div className="card-header pt-5">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold text-dark">Rate Metrics</span>
                        <span className="text-gray-400 mt-1 fw-semibold fs-6">CTR, bounce rate, scroll depth &amp; conversion</span>
                      </h3>
                    </div>
                    <div className="card-body pt-4 pb-6">
                      <div className="row g-6">
                        {[
                          {
                            label: 'Click Through Rate',
                            value: financialMetrics?.click_through_rate ?? '0%',
                            raw: parseFloat(financialMetrics?.click_through_rate ?? '0'),
                            max: 20,
                          },
                          {
                            label: 'Bounce Rate',
                            value: adPerformance?.bounce_rate ?? '0%',
                            raw: parseFloat(adPerformance?.bounce_rate ?? '0'),
                            max: 100,
                            danger: true,
                          },
                          {
                            label: 'Scroll Depth',
                            value: engagementMetrics?.scroll_depth ?? '0%',
                            raw: parseFloat(engagementMetrics?.scroll_depth ?? '0'),
                            max: 100,
                          },
                          {
                            label: 'Conversion Rate',
                            value: conversionAndContact?.conversion_rate ?? '0%',
                            raw: parseFloat(conversionAndContact?.conversion_rate ?? '0'),
                            max: 20,
                          },
                        ].map((metric, i) => (
                          <div className="col-12 col-md-6" key={i}>
                            <div className="d-flex justify-content-between mb-2">
                              <span className="fw-semibold text-gray-700 fs-6">{metric.label}</span>
                              <span className="fw-bold text-dark fs-6">{metric.value}</span>
                            </div>
                            <div className="h-8px bg-gray-200 rounded">
                              <div
                                className={`h-8px rounded`}
                                style={{
                                  width: `${Math.min(100, (metric.raw / metric.max) * 100)}%`,
                                  backgroundColor: metric.danger ? (metric.raw > 60 ? '#f1416c' : '#396A54') : '#396A54',
                                  transition: 'width 0.6s ease',
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAdDetails;
